import { useState, useRef, useEffect, useCallback } from "react";
import { Camera, X } from "lucide-react";

export default function QrScanner({ onScan, onClose }) {
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);
  const mountedRef = useRef(true);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    mountedRef.current = true;
    let scanner = null;

    const startScanner = async () => {
      // Small delay to ensure DOM is ready
      await new Promise(r => setTimeout(r, 300));
      if (!mountedRef.current) return;

      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (!mountedRef.current) return;

        const el = document.getElementById("qr-reader-container");
        if (!el) { setError("Scanner container not found"); return; }

        scanner = new Html5Qrcode("qr-reader-container");
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 220, height: 220 }, aspectRatio: 1.0 },
          (decodedText) => {
            if (scannerRef.current) {
              scannerRef.current.stop().catch(() => {});
              scannerRef.current = null;
            }
            if (mountedRef.current) {
              onScanRef.current(decodedText);
            }
          },
          () => {}
        );
      } catch (err) {
        if (mountedRef.current) {
          setError("Camera access denied or not available.");
        }
      }
    };

    startScanner();

    return () => {
      mountedRef.current = false;
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex flex-col items-center justify-center p-2 sm:p-4" data-testid="qr-scanner-overlay">
      <div className="bg-white rounded-2xl overflow-hidden w-full max-w-md flex flex-col max-h-[95vh]">
        <div className="flex items-center justify-between p-3 border-b shrink-0">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-[#1e3a5f]" />
            <span className="font-bold text-sm text-[#1e3a5f]">Scan QR Code</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100 -mr-1" data-testid="qr-close-btn" aria-label="Close scanner">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        {/* aspect-square keeps a stable box; on tall phones we cap at 60vh so
            the header + footer stay visible without scrolling. */}
        <div className="w-full max-w-[60vh] mx-auto aspect-square bg-black overflow-hidden">
          <div id="qr-reader-container" className="w-full h-full [&_video]:!w-full [&_video]:!h-full [&_video]:object-cover" />
        </div>
        {error && (
          <div className="p-4 text-center shrink-0">
            <p className="text-red-500 text-sm mb-2">{error}</p>
            <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-200 text-sm font-medium">Close</button>
          </div>
        )}
        <p className="text-center text-xs text-slate-400 py-2 px-3 shrink-0">Point camera at a guest or member QR code</p>
      </div>
    </div>
  );
}
